/**
 * Identify the current user to Amplitude with Firebase UID and business UID.
 * Call this when the user signs in and their business context is loaded (e.g. from ProfileContext).
 * - Sets Amplitude user ID to the Firebase UID (stable per person).
 * - Sets user properties so you can segment by business_uid in Amplitude.
 */

declare global {
  interface Window {
    amplitude?: {
      setUserId: (userId: string) => void
      identify: (identify: { set: (key: string, value: unknown) => unknown }) => void
      Identify?: new () => {
        set: (key: string, value: unknown) => unknown
      }
      reset: () => void
    }
  }
}

/**
 * Identify the current user in Amplitude with their Firebase UID and business UID.
 * Use this after auth and profile load (e.g. in ProfileContext when businessProfile is set).
 */
export function identifyAmplitudeUser(
  firebaseUid: string,
  businessUid: string | null,
  options?: { email?: string; name?: string }
): void {
  const amp = typeof window !== "undefined" ? window.amplitude : undefined
  if (!amp?.setUserId) return

  try {
    amp.setUserId(firebaseUid)

    const Identify = amp.Identify
    if (Identify) {
      const identify = new Identify()
      identify.set("business_uid", businessUid ?? "")
      if (options?.email) identify.set("email", options.email)
      if (options?.name) identify.set("name", options.name)
      amp.identify(identify)
    }
  } catch (e) {
    console.warn("[Amplitude] identify user failed:", e)
  }
}

/**
 * Clear Amplitude user identity (e.g. on sign out).
 * Resets device user id so next identify is a new user.
 */
export function resetAmplitudeUser(): void {
  const amp = typeof window !== "undefined" ? window.amplitude : undefined
  if (!amp?.reset) return
  try {
    amp.reset()
  } catch (e) {
    console.warn("[Amplitude] reset failed:", e)
  }
}
